import { Icon, SvgIconNames } from '@toggle/design-system';
import React, { RefObject, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '~/components/filter/Option';
import { FilterOption } from '~/components/update-filter-options/updateFilterOptions';
import { useReasonerParams } from '~/views/alfred/research/hooks/useReasonerParams';
import { SubmittedAnalysisMode } from '~/views/alfred/research/services/conversations-service';
import { useChat } from '~/views/alfred/research/use-chat/useChat';

import * as S from './AnalysisModeDropdown.styles';

interface DropdownOption {
  key: string;
  icon: SvgIconNames;
  name: string;
  value: {
    analysis_mode: SubmittedAnalysisMode;
  };
  description: string;
}

export interface AnalysisModeDropdownProps {
  isMessagePending: boolean;
  contentRef: RefObject<HTMLDivElement | null>;
}

export const AnalysisModeDropdown = ({
  isMessagePending,
  contentRef,
}: AnalysisModeDropdownProps) => {
  const { t } = useTranslation(['chat']);

  const { conversationId } = useReasonerParams();

  const { analysisMode, webSearch, activeConversation, setAnalysisMode } =
    useChat(state => ({
      analysisMode: state.analysisMode,
      webSearch: state.webSearch,
      activeConversation: state.getActiveConversation(conversationId),
      setAnalysisMode: state.setAnalysisMode,
    }));

  const renderCustomFilterOptionLabel = (filterOption: FilterOption) => {
    return (
      <S.OptionWrapper>
        {filterOption.icon && <Icon iconName={filterOption.icon} size={16} />}
        <S.Label>{filterOption.name}</S.Label>
      </S.OptionWrapper>
    );
  };

  const options: DropdownOption[] = [
    {
      key: 'auto',
      icon: 'QuickAnalysis',
      name: t('chat:analysisMode.autoTitle'),
      value: {
        analysis_mode: 'Auto',
      },
      description: t('chat:analysisMode.autoDescription'),
    },
    {
      key: 'deep',
      icon: 'Alfred',
      name: t('chat:analysisMode.deepTitle'),
      value: {
        analysis_mode: 'Deep',
      },
      description: t('chat:analysisMode.deepDescription'),
    },
  ];

  const selectedOption = (() => {
    let option = options.find(i => i.value.analysis_mode === analysisMode);

    const lastTask = activeConversation?.tasks?.at(-1);

    if (lastTask?.submittedAnalysisMode && isMessagePending) {
      option = options.find(
        i => i.value.analysis_mode === lastTask.submittedAnalysisMode
      );
    }

    return option;
  })();

  const forceToDeep = webSearch === 'restricted' || webSearch === 'block';

  useEffect(() => {
    if (forceToDeep) {
      setAnalysisMode('Deep');
    }
  }, [webSearch]);

  return (
    <S.StyledDropdownMenu
      contentRef={contentRef}
      triggerProps={{
        disabled: isMessagePending || forceToDeep,
      }}
      triggerNode={
        <div>
          <S.StyledChip
            label={selectedOption?.name}
            leftIcon={selectedOption?.icon}
            rightIcon="ChevronLightDown"
            rightIconSize={8}
            disabled={isMessagePending}
            prefixNode={
              <S.PrefixText>{t('chat:analysisMode.prefix')}</S.PrefixText>
            }
            data-testid="analysis-mode-dropdown-trigger"
          />
        </div>
      }
    >
      {options.map(i => {
        return (
          <Option
            key={i.key}
            option={i}
            isChecked={analysisMode === i.value.analysis_mode}
            indentedDescription={24}
            handleClick={() => {
              setAnalysisMode(i.value.analysis_mode);
            }}
            renderCustomFilterOptionLabel={renderCustomFilterOptionLabel}
          />
        );
      })}
    </S.StyledDropdownMenu>
  );
};
